/**
 * Shared de-DE / Europe-Berlin formatting singletons (BUILD-SPEC §4.6).
 *
 * The whole UI is German-only with no i18n framework, so every amount and date
 * is formatted through the module-level `Intl` instances below. They are
 * created once (constructing an `Intl` formatter is comparatively expensive)
 * and reused everywhere.
 */

/** Currency amounts, e.g. `1.234,50 €`. */
export const eur = new Intl.NumberFormat('de-DE', {
  style: 'currency',
  currency: 'EUR',
});

/** Dates in Europe/Berlin, e.g. `12.07.2026`. */
const dateFormat = new Intl.DateTimeFormat('de-DE', {
  timeZone: 'Europe/Berlin',
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
});

/** Format a `Date`, an ISO string, or an epoch value as a de-DE date. */
export function formatDate(value: Date | string | number): string {
  const date = value instanceof Date ? value : new Date(value);
  return dateFormat.format(date);
}

/** Date + time in Europe/Berlin, e.g. `12.07.2026, 14:05`. */
const dateTimeFormat = new Intl.DateTimeFormat('de-DE', {
  timeZone: 'Europe/Berlin',
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
  hour: '2-digit',
  minute: '2-digit',
});

/** Format a timestamp (ISO string, `Date`, or epoch) as a de-DE date+time. */
export function formatDateTime(value: Date | string | number): string {
  const date = value instanceof Date ? value : new Date(value);
  return dateTimeFormat.format(date);
}

/**
 * Format a signed Money string (or number) as a de-DE Euro amount.
 *
 * This is the ONLY place a Money string is parsed to a number, and it is
 * display-only — wire values stay decimal strings everywhere else. Invalid
 * input degrades to `0,00 €` rather than `NaN €`.
 */
export function money(value: string | number): string {
  const n = typeof value === 'number' ? value : Number.parseFloat(value);
  return eur.format(Number.isFinite(n) ? n : 0);
}

/** Euro without decimals, e.g. `2.500 €` — for compact chart axis ticks. */
export const eurShort = new Intl.NumberFormat('de-DE', {
  style: 'currency',
  currency: 'EUR',
  maximumFractionDigits: 0,
});

/**
 * Compact Euro amount (no decimals) for dense chart tick labels, e.g. `2.500 €`.
 * Same degrade-to-`0 €` contract as `money`.
 */
export function moneyShort(value: string | number): string {
  const n = typeof value === 'number' ? value : Number.parseFloat(value);
  return eurShort.format(Number.isFinite(n) ? n : 0);
}

// ── User-input amount parsing (single shared German parser) ─────────────────

/** de-DE editing formatter for amount inputs (always 2dp, no grouping). */
const amountInputFormat = new Intl.NumberFormat('de-DE', {
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
  useGrouping: false,
});

/**
 * Parse a user-entered German amount into the canonical dot-decimal wire
 * string the API expects (`"1.234,56"` / `"1234,56"` / `"1234.56"` / `"-5"` →
 * `"1234.56"` / `"-5.00"`), or `null` when the input is not a valid amount.
 *
 * Rules: whitespace and a `€` sign are tolerated; when a comma is present it
 * is the decimal separator and dots are thousands groupers; without a comma a
 * dot is the decimal separator; at most two decimal places; optional leading
 * minus. This is the ONE amount parser — every amount input (split editor,
 * dialogs, budget input, filters) must go through it (no divergent copies).
 */
export function parseAmountInput(raw: string): string | null {
  let s = raw.replace(/\s/g, '').replace(/€/g, '');
  if (s === '') return null;
  // de-DE: a comma is the decimal separator and dots are thousands groupers.
  if (s.includes(',')) s = s.replace(/\./g, '').replace(',', '.');
  if (!/^-?\d+(\.\d{1,2})?$/.test(s)) return null;
  const negative = s.startsWith('-');
  const [intPart, fracPart = ''] = (negative ? s.slice(1) : s).split('.');
  return `${negative ? '-' : ''}${intPart}.${(fracPart + '00').slice(0, 2)}`;
}

/** Parse a user-entered German amount to signed integer cents, or `null`. */
export function parseAmountToCents(raw: string): number | null {
  const s = parseAmountInput(raw);
  if (s === null) return null;
  const negative = s.startsWith('-');
  const [intPart, fracPart] = (negative ? s.slice(1) : s).split('.');
  const cents = Number(intPart) * 100 + Number(fracPart);
  return negative ? -cents : cents;
}

/**
 * Render a canonical Money string (or number) as the de-DE editing value for
 * an amount input, e.g. `"-49.90"` → `"-49,90"` — the sign-preserving inverse
 * of `parseAmountInput`. Invalid input degrades to `''`.
 */
export function formatAmountInput(value: string | number): string {
  const n = typeof value === 'number' ? value : Number.parseFloat(value);
  return Number.isFinite(n) ? amountInputFormat.format(n) : '';
}

const monthLongFormat = new Intl.DateTimeFormat('de-DE', {
  timeZone: 'Europe/Berlin',
  month: 'long',
  year: 'numeric',
});

const monthShortFormat = new Intl.DateTimeFormat('de-DE', {
  timeZone: 'Europe/Berlin',
  month: 'short',
  year: 'numeric',
});

/** Parse a canonical `YYYY-MM` month to a mid-month Berlin date (noon UTC → no
 * tz drift across the month boundary), or `null` if malformed. */
function monthToDate(month: string): Date | null {
  const m = /^(\d{4})-(0[1-9]|1[0-2])$/.exec(month);
  if (!m) return null;
  return new Date(Date.UTC(Number(m[1]), Number(m[2]) - 1, 15, 12));
}

/** Format a `YYYY-MM` month as a de-DE long month, e.g. `2026-07` → `Juli 2026`. */
export function formatMonth(month: string): string {
  const d = monthToDate(month);
  return d ? monthLongFormat.format(d) : month;
}

/** Format a `YYYY-MM` month as a de-DE short month, e.g. `2026-07` → `Juli 2026`
 * (short form) — used for dense chart x-axes. */
export function formatMonthShort(month: string): string {
  const d = monthToDate(month);
  return d ? monthShortFormat.format(d) : month;
}
